const valid = (p) => p && ['x', 'y', 'z'].every((axis) => Number.isFinite(p[axis]));

/** A single bounded volumetric cone projects the domain's physical lamp ray.
 * Its origin, illuminated endpoint and active state have no presentation clock.
 * The actual articulated wreck supplies the lamp; this is only light in mist. */
export function createSkyBrideSearchlights(T, scene, own, reduced = () => false) {
  const geometry = own(new T.CylinderGeometry(1, 0.045, 1, 24, 1, true));
  const material = own(
    new T.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: T.AdditiveBlending,
      side: T.DoubleSide,
      uniforms: { density: { value: 0.12 } },
      vertexShader: `
      varying vec2 beamUv;
      varying vec3 beamNormal;
      varying vec3 beamPoint;
      void main(){
        beamUv=uv;
        vec4 view=modelViewMatrix*vec4(position,1.0);
        beamPoint=view.xyz;
        beamNormal=normalize(normalMatrix*normal);
        gl_Position=projectionMatrix*view;
      }`,
      fragmentShader: `
      uniform float density;
      varying vec2 beamUv;
      varying vec3 beamNormal;
      varying vec3 beamPoint;
      void main(){
        vec3 viewDirection=normalize(-beamPoint);
        float facing=abs(dot(normalize(beamNormal),viewDirection));
        float radial=pow(facing,1.35);
        float endFade=1.0-smoothstep(.80,1.0,beamUv.y);
        float alpha=density*radial*endFade;
        gl_FragColor=vec4(vec3(.48,.70,.78),alpha);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
    }),
  );
  const mesh = new T.Mesh(geometry, material),
    up = new T.Vector3(0, 1, 0),
    direction = new T.Vector3();
  mesh.name = 'Wreck searchlight atmospheric beam';
  mesh.visible = false;
  mesh.frustumCulled = false;
  mesh.renderOrder = 3;
  scene.add(mesh);
  return {
    draw(state, visible = true) {
      const beam = visible && !state.boss?.defeated ? state.boss?.mourning?.beams?.[0] : null;
      mesh.visible = Boolean(beam && valid(beam.from) && valid(beam.to));
      if (!mesh.visible) return;
      direction.set(beam.to.x - beam.from.x, beam.to.y - beam.from.y, beam.to.z - beam.from.z);
      const length = direction.length();
      mesh.visible = length > 0.01;
      if (!mesh.visible) return;
      mesh.position.set(
        (beam.from.x + beam.to.x) / 2,
        (beam.from.y + beam.to.y) / 2,
        (beam.from.z + beam.to.z) / 2,
      );
      mesh.quaternion.setFromUnitVectors(up, direction.normalize());
      mesh.scale.set(Math.min(2, length * 0.035), length, Math.min(2, length * 0.035));
      material.uniforms.density.value = reduced() ? 0.075 : 0.12;
    },
    dispose() {
      scene.remove(mesh);
    },
  };
}
